import React, { useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  HStack,
  Icon,
  NativeSelect,
  Spacer,
  Stack,
  StackSeparator,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { t } from "i18next";
import { nodeReferences } from "../../../../osaway/stores/OsaNodeStore";
import {
  CommentFilterOption,
  filterCommentByOption,
  isNodeComment,
  isOsaWayComment,
} from "../../../../coding/utils/comments-utils";
import { CommentsDashboardModal } from "../../../../coding/views/dashboard-modal/CommentsDashboardModal";
import { CommentCreator } from "../../../../coding/views/inputs/CommentCreator";
import { Comment } from "../../../../coding/views/Comment";
import { useStores } from "../../../../shared/stores/RootStore";
import { AddIcon } from "../../../../shared/views/icons";
import { LifeCycleStage } from "../../../../osaway/models/LifeCycleStage";
import { ToggleVisibilityButton } from "../../ToggleVisibilityButton";
import { MapComponent } from "../../../../project/stores/MapStore";

const i18prefix = "map:bottomBar.comments";

const ViewCommentsBase: React.FC = () => {
  const { osaCommentStore, osaWayStore, osaNodeStore, mapStore } = useStores();
  const commentCreator = useDisclosure();
  const commentsDashboardModal = useDisclosure();
  const [filterOption, setFilterOption] = useState(
    CommentFilterOption.Unresolved,
  );
  const selectedNode = osaNodeStore.getSelected()!;
  const selectedWay = osaWayStore.getSelected()!;

  const filteredComments = useMemo(() => {
    if (!selectedWay) return [];

    const wayComments =
      osaCommentStore.commentsByOsaWayId.get(selectedWay.id) ?? [];

    return wayComments.filter((comment) => {
      if (selectedNode) {
        if (!isNodeComment(comment, selectedNode.id!)) return false;
      } else if (!isOsaWayComment(comment)) return false;
      return filterCommentByOption(comment, filterOption);
    });

    //eslint-disable-next-line
  }, [
    osaCommentStore.commentsByOsaWayId,
    filterOption,
    selectedWay,
    selectedNode,
  ]);

  if (!selectedWay.permissions?.canComment) {
    return (
      <Box p={3} width={"full"}>
        <Alert.Root>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>You don't have access to the comments</Alert.Title>

            <Alert.Description>
              {selectedWay.lifeCycleStage === LifeCycleStage.Coding ||
              selectedWay.lifeCycleStage === LifeCycleStage.Review
                ? "To get access, speak to a project manager"
                : 'The life cycle stage must be "Coding" or "Review"'}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      </Box>
    );
  }

  return (
    <Stack pb={4} overflowY="scroll" flexShrink={0} maxH={"400px"} gap={0}>
      <ToggleVisibilityButton
        component={MapComponent.Comments}
        arrowAngleWhenOpen={90}
      />

      <HStack
        bg="white"
        position="sticky"
        top={0}
        zIndex={1}
        pt={4}
        px={4}
        gap={4}
      >
        <Box>
          <Text>Comments</Text>

          <Text fontWeight="semibold" fontSize="xs" textTransform="uppercase">
            {selectedNode
              ? t(`${i18prefix}.title.node`, {
                  number: nodeReferences.get(selectedNode.id)!.index + 1,
                })
              : t(`${i18prefix}.title.route`)}
          </Text>
        </Box>

        <Spacer />

        <ButtonGroup
          size="sm"
          fontSize={"sm"}
          variant="outline"
          colorPalette="primary"
          attached
        >
          <Button
            onClick={() => {
              commentCreator.onOpen();

              mapStore.setComponentVisibility((prev) => ({
                ...prev,
                comments: true,
              }));
            }}
          >
            <Icon as={AddIcon} /> Write
          </Button>

          <Button onClick={commentsDashboardModal.onOpen}>View all</Button>
        </ButtonGroup>

        <HStack>
          <Text fontSize="sm">{t(`${i18prefix}.filter`)}</Text>
          <NativeSelect.Root size="sm" fontSize={"sm"}>
            <NativeSelect.Field
              value={filterOption}
              onChange={(e) =>
                setFilterOption(e.target.value as CommentFilterOption)
              }
            >
              <option value={CommentFilterOption.Unresolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Unresolved}`,
                )}
              </option>
              <option value={CommentFilterOption.Resolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Resolved}`,
                )}
              </option>
              <option value={CommentFilterOption.All}>
                {t(`map:filters.commentOptions.${CommentFilterOption.All}`)}
              </option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </HStack>
      </HStack>

      {mapStore.componentVisibility.comments &&
        (filteredComments.length > 0 ? (
          <Stack py={2} px={4} gap={2} separator={<StackSeparator />}>
            {filteredComments.map((comment, i) => (
              <Comment comment={comment} key={i} />
            ))}
          </Stack>
        ) : (
          <Text px={4} mt={1} fontSize="sm">
            {t(`${i18prefix}.noComments`)}
          </Text>
        ))}

      {commentCreator.open && (
        <Box p={4}>
          <CommentCreator
            osaWayId={selectedWay?.id}
            nodeId={selectedNode?.id}
            onClose={commentCreator.onClose}
          />
        </Box>
      )}

      {commentsDashboardModal.open && (
        <CommentsDashboardModal onClose={commentsDashboardModal.onClose} />
      )}
    </Stack>
  );
};

export const ViewComments = observer(ViewCommentsBase);
